import { useState } from 'react';
import {
  getCalendarEvents,
  getCalendarMetadata,
  getKeyPickupEventBySuite,
} from 'src/services/client';
import { formatApiEventsToCalendarEvents } from 'src/pages/Calendar/helpers';
import {
  ICalendarEvent,
  ICalendarEventData,
  ICalendarEventsServiceResponse,
  ICalendarMetadataApiResult,
  ICalendarMetadataData,
  IKeyPickupEventServiceResponse,
  IProperty,
} from 'src/pages/Calendar/types';

export interface IUseCalendarDataReturn {
  events: ICalendarEvent[];
  availableProperties: IProperty[];
  availableEventTypes: number[];
  isLoadingEvents: boolean;
  eventsError: string | null;
  keyPickupEvent: ICalendarEventData | null;
  fetchEvents: (from: Date, to: Date) => Promise<void>;
  fetchMetadata: () => Promise<void>;
  fetchKeyPickupEventBySuite: (suiteId: string) => Promise<void>;
}

export const useCalendarData = (): IUseCalendarDataReturn => {
  const [events, setEvents] = useState<ICalendarEvent[]>([]);
  const [availableProperties, setAvailableProperties] = useState<IProperty[]>(
    []
  );
  const [availableEventTypes, setAvailableEventTypes] = useState<number[]>([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState<boolean>(false);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [keyPickupEvent, setKeyPickupEvent] =
    useState<ICalendarEventData | null>(null);

  const fetchMetadata = async (): Promise<void> => {
    try {
      const response: ICalendarMetadataApiResult = await getCalendarMetadata();

      if (response.ok && response.data) {
        const metadata: ICalendarMetadataData = response.data.data;
        setAvailableProperties(metadata.availableProperties || []);
        setAvailableEventTypes(metadata.availableEventTypes || []);
        return;
      }

      console.error('Failed to fetch calendar metadata:', response.error);
    } catch (error) {
      console.error('Error fetching calendar metadata:', error);
    }
  };

  const fetchEvents = async (from: Date, to: Date): Promise<void> => {
    setIsLoadingEvents(true);
    setEventsError(null);

    try {
      const formattedFrom: string = from.toISOString().split('T')[0];
      const formattedTo: string = to.toISOString().split('T')[0];

      const response: ICalendarEventsServiceResponse = await getCalendarEvents(
        formattedFrom,
        formattedTo
      );

      const eventsData: ICalendarEventData[] = response?.data?.data;

      if (eventsData && Array.isArray(eventsData)) {
        const formattedEvents: ICalendarEvent[] =
          formatApiEventsToCalendarEvents(eventsData);

        setEvents(formattedEvents);
      }
    } catch (error) {
      console.error('Error fetching calendar events:', error);
      setEventsError('Failed to load calendar events. Please try again later.');
    } finally {
      setIsLoadingEvents(false);
    }
  };

  const fetchKeyPickupEventBySuite = async (suiteId: string): Promise<void> => {
    const response: IKeyPickupEventServiceResponse =
      await getKeyPickupEventBySuite(suiteId);

    if (response.ok && response.data) {
      setKeyPickupEvent(response.data.data);
      return;
    }

    setKeyPickupEvent(null);
  };

  return {
    events,
    availableProperties,
    availableEventTypes,
    isLoadingEvents,
    eventsError,
    keyPickupEvent,
    fetchEvents,
    fetchMetadata,
    fetchKeyPickupEventBySuite,
  };
};